/**
 * Web-push (FCM) client plumbing — available to every user.
 *
 * Keeps `firebase/messaging` out of the main bundle via dynamic import (per
 * CLAUDE.md — heavy, feature-specific libs are loaded on demand). Only the
 * Settings → Notifications section calls these, and the messaging chunk loads
 * lazily when that section mounts, so it never weighs on first paint.
 *
 * Token storage: one doc per device under `users/{uid}/pushTokens/{tokenId}`.
 * `tokenId` is a stable hash of the FCM token (which is too long / unsafe to
 * use verbatim as a doc id), so re-enabling on the same device overwrites the
 * same doc instead of piling up duplicates. The notification Cloud Functions
 * read this subcollection with the admin SDK and prune stale tokens.
 */
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db, app } from "../firebase";
import { callFunction } from "./callFunction";
import { localStore } from "./safeStorage";
import { installedGymAppId } from "./pwaApp";

const VAPID_KEY = import.meta.env.VITE_FB_VAPID_KEY;
const TOKEN_ID_LS_KEY = "sendly-push-token-id";

/**
 * The token doc's `appGymId`: which gym's installed app registered it (null =
 * the Sendly app or a browser tab), so the push server can title that app's
 * pushes by category instead of repeating the gym the app is named after.
 * While a gym app's gym is still loading the field is left out — the write
 * merges, so the previous value stays rather than a wrong null.
 */
function appFields() {
  const appGymId = installedGymAppId();
  return appGymId === undefined ? {} : { appGymId };
}

// FNV-1a → base36. Deterministic, dependency-free, good enough to derive a
// short collision-resistant doc id from a unique FCM token at our scale.
function hashToken(token) {
  let h = 0x811c9dc5;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return "t_" + (h >>> 0).toString(36) + "_" + token.length.toString(36);
}

/** True when this browser can register for web push at all. */
export async function isPushSupported() {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return false;
  try {
    const { isSupported } = await import("firebase/messaging");
    return await isSupported();
  } catch {
    return false;
  }
}

// Module-level cache of the messaging instance + active SW registration.
// iOS Safari is strict about user-gesture continuity: doing the dynamic
// import + getMessaging + serviceWorker.ready awaits INSIDE the button click,
// before getToken, can break the gesture chain and make the FCM registration
// fetch reject with "Load failed". Pre-warming on mount means the click path
// is just requestPermission → getToken with no slow awaits in between.
let _messaging = null;
let _registration = null;
let _getTokenFn = null;

/**
 * Pre-load the messaging SDK and resolve the active service worker. Call this
 * on mount (when the user might enable push) so enablePush() runs lean.
 */
export async function warmupPush() {
  if (!(await isPushSupported())) return false;
  try {
    const mod = await import("firebase/messaging");
    _getTokenFn = mod.getToken;
    if (!_messaging) _messaging = mod.getMessaging(app);
    if (!_registration && "serviceWorker" in navigator) {
      _registration = await navigator.serviceWorker.ready;
    }
    return !!(_messaging && _registration);
  } catch (e) {
    console.error("[push] warmup failed", e);
    return false;
  }
}

/** Current Notification permission ("granted" | "denied" | "default" | "unsupported"). */
export function getPermissionState() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/** localStorage-cached id of the token registered on this device (or null). */
export function getRegisteredTokenId() {
  return localStore.get(TOKEN_ID_LS_KEY);
}

/**
 * Request permission, obtain an FCM token bound to the app's existing service
 * worker, and persist it. Returns { ok, permission, tokenId?, error? }.
 */
export async function enablePush(uid) {
  if (!uid) return { ok: false, error: "no-uid" };
  if (!VAPID_KEY) return { ok: false, error: "no-vapid-key" };

  // Request permission FIRST, directly in the gesture, before any heavy work.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, permission, error: "permission" };

  // Ensure the SDK + SW are ready. Normally warmupPush() already ran on mount,
  // so this is a no-op; if not, do it now (costs the gesture, but correctness
  // first).
  if (!_messaging || !_registration || !_getTokenFn) {
    const warm = await warmupPush();
    if (!warm) return { ok: false, permission, error: "unsupported" };
  }

  // Step 1 — obtain the FCM token. Isolated so a getToken failure (bad VAPID,
  // unsupported context, push-subscribe rejection on iOS) reports a distinct
  // `gettoken:<code>` rather than being confused with a Firestore write error.
  let token;
  try {
    token = await _getTokenFn(_messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: _registration });
  } catch (e) {
    console.error("[push] getToken failed", e);
    return { ok: false, permission, error: "gettoken:" + (e?.code || e?.message || "unknown") };
  }
  if (!token) return { ok: false, permission, error: "no-token" };

  // Step 2 — persist the token. A permission-denied here means firestore.rules
  // for users/{uid}/pushTokens isn't deployed.
  try {
    const tokenId = hashToken(token);
    await setDoc(doc(db, "users", uid, "pushTokens", tokenId), {
      token,
      platform: navigator.platform || "",
      ua: navigator.userAgent || "",
      ...appFields(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    localStore.set(TOKEN_ID_LS_KEY, tokenId);
    console.log("[push] token registered", tokenId);
    return { ok: true, permission, tokenId };
  } catch (e) {
    console.error("[push] token Firestore write failed", e);
    return { ok: false, permission, error: "write:" + (e?.code || e?.message || "unknown") };
  }
}

/**
 * Silent self-heal: re-fetch the current FCM token and rewrite its Firestore
 * doc. Call on mount when this device claims (via localStorage) to be
 * registered — guards against a "registered" UI state that has no live token
 * behind it (token rotated/expired, or its doc was pruned as stale), which
 * otherwise silently drops every notification until the user re-toggles.
 *
 * Never prompts: bails unless permission is already "granted". Returns
 * { ok:false } (and clears the stale local flag) when no valid token exists, so
 * the caller can flip the UI back to "not registered".
 */
export async function refreshPushToken(uid) {
  if (!uid || !VAPID_KEY) return { ok: false };
  if (getPermissionState() !== "granted") return { ok: false, reason: "permission" };
  if (!(await warmupPush())) return { ok: false };
  try {
    const token = await _getTokenFn(_messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: _registration });
    if (!token) {
      localStore.remove(TOKEN_ID_LS_KEY);
      return { ok: false };
    }
    const tokenId = hashToken(token);
    await setDoc(doc(db, "users", uid, "pushTokens", tokenId), {
      token,
      platform: navigator.platform || "",
      ua: navigator.userAgent || "",
      ...appFields(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    localStore.set(TOKEN_ID_LS_KEY, tokenId);
    return { ok: true, tokenId };
  } catch (e) {
    console.error("[push] refresh failed", e);
    return { ok: false };
  }
}

/**
 * Tell the backend the user has seen their notifications (app opened/focused).
 * Best-effort cross-device clear: the markNotificationsSeen callable sends a
 * "clear" push to the user's OTHER devices, but only when there are unseen
 * notifications.
 *
 * By default this no-ops on a device that never registered for push (nothing to
 * coordinate). Pass `allowWithoutToken` for users who DO use push elsewhere
 * (the caller gates this on having received a push before — `pushMeta
 * .lastNotifiedAt`): then even a non-registered device — e.g. the user's
 * desktop browser — calls through with tokenId=null, so the CF excludes no
 * device and clears the notification on ALL of the user's registered devices.
 * This is what makes "open the app on any device → it disappears on the others"
 * work. The gate keeps us from firing a per-focus callable for users who never
 * use push.
 */
export async function markSeen({ allowWithoutToken = false } = {}) {
  if (typeof window === "undefined") return;
  const tokenId = getRegisteredTokenId();
  if (!tokenId && !allowWithoutToken) return; // not push-relevant — nothing to coordinate
  try {
    await callFunction("markNotificationsSeen", { tokenId: tokenId || null });
  } catch (_) { /* best effort */ }
}

/** Delete the FCM token and its Firestore doc for this device. */
export async function disablePush(uid) {
  const tokenId = getRegisteredTokenId();
  try {
    const { getMessaging, deleteToken } = await import("firebase/messaging");
    await deleteToken(getMessaging(app));
  } catch { /* token may already be gone — proceed to clean Firestore */ }
  try {
    if (uid && tokenId) await deleteDoc(doc(db, "users", uid, "pushTokens", tokenId));
  } catch { /* best effort */ }
  localStore.remove(TOKEN_ID_LS_KEY);
  return { ok: true };
}
